import { useState } from "react";

import { Box, Flex } from "@chakra-ui/react";

import { PhotoIcon, RetryIcon, TrashIcon } from "ui/Icons/Icons";
import { Spinner } from "ui/Spinner/Spinner";
import { BodyS, DetailL } from "ui/Typography/Typography";

export type Photo = {
  id: string;
  title: string;
  src: string;
  status: "loading" | "error" | "success";
};

export type PhotoUploadProps = {
  label: string | React.ReactNode;
  uploadedPhotos: Photo[];
  onPhotoUpload: (arg0: File) => Promise<void>;
  onPhotoRemove: (arg0: string) => Promise<void>;
  maxPhotos?: number;
  isDisabled?: boolean;
};

function AddPhotoButton({
  label,
  fullSize,
  onClick,
  isDisabled,
}: {
  label: string | React.ReactNode;
  fullSize: boolean;
  onClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isDisabled?: boolean;
}) {
  return (
    <Flex
      as="label"
      position="relative"
      w={fullSize ? "100%" : "80px"}
      h={fullSize ? "100px" : "80px"}
      gap="s100"
      shrink="0"
      direction="column"
      alignItems="center"
      justifyContent="center"
      bgColor="grey.100"
      borderRadius="8px"
      cursor={isDisabled ? "not-allowed" : "pointer"}
      opacity={isDisabled ? 0.4 : 1}
      htmlFor="photo-input"
    >
      <Box
        as="input"
        id="photo-input"
        type="file"
        capture="environment"
        accept="image/*"
        onChange={onClick}
        aria-label="Add photo"
        opacity="0"
        width="80px"
        position="absolute"
        disabled={isDisabled}
      />
      <PhotoIcon name="m" boxSize={6} />
      <BodyS fontWeight="bold" textAlign="center">
        {label}
      </BodyS>
    </Flex>
  );
}

function PhotoPreview({
  src,
  title,
  isLoading,
  hasError,
  action,
  actionLabel,
  actionIcon,
}: {
  src: string;
  title: string;
  isLoading?: boolean;
  hasError?: boolean;
  action?: (args: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  actionLabel?: string;
  actionIcon?: React.ReactNode;
}) {
  return (
    <Flex
      position="relative"
      w="80px"
      h="80px"
      shrink="0"
      alignItems="center"
      justifyContent="center"
      borderRadius="8px"
      overflow="hidden"
      border={hasError ? "2px solid red" : "none"}
      borderColor="red.500"
    >
      <Box
        as="img"
        src={src}
        alt={title}
        position="absolute"
        zIndex="1"
        top="0"
        left="0"
        w="100%"
        h="100%"
        objectFit="cover"
      />
      <Box
        position="absolute"
        zIndex="5"
        top="0"
        left="0"
        w="100%"
        h="100%"
        bg="rgba(0, 0, 0, 0.5)"
      />
      {isLoading ? (
        <Spinner w="36px" h="36px" position="relative" zIndex="10" />
      ) : (
        <Box
          as="button"
          position="relative"
          zIndex="10"
          w="36px"
          h="36px"
          bgColor="grey.100"
          borderRadius="100%"
          onClick={action}
          aria-label={actionLabel}
        >
          {actionIcon}
        </Box>
      )}
    </Flex>
  );
}

export function PhotoUpload({
  label,
  uploadedPhotos,
  onPhotoUpload,
  onPhotoRemove,
  maxPhotos = 3,
  isDisabled = false,
}: PhotoUploadProps) {
  const [localPreviews, setLocalPreviews] = useState<(Photo & { file: File })[]>([]);

  const [errorMessage, setErrorMessage] = useState<string | null>();

  const photosDisplayedLength = uploadedPhotos.length + localPreviews.length;

  const canAddMore = photosDisplayedLength < maxPhotos;

  const removeLocalPreview = (localPreviewId: string) => {
    setLocalPreviews((prev) => prev.filter(({ id }) => id !== localPreviewId));
  };

  const setLocalPreviewStatus = (
    status: "loading" | "error" | "success",
    localPreviewId: string,
  ) => {
    setLocalPreviews((prev) =>
      prev.map(({ id, ...rest }) =>
        id === localPreviewId ? { id, ...rest, status } : { id, ...rest },
      ),
    );
  };

  const handlePhotoRemove = async (id: string) => {
    try {
      await onPhotoRemove(id);

      setErrorMessage(null);
    } catch (error: Error | unknown) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("An error occurred while removing the photo.");
      }
    }
  };

  const handlePhotoUpload = async (photoToUpload: File) => {
    const file = photoToUpload;
    setLocalPreviewStatus("loading", file.name);

    try {
      await onPhotoUpload(file);

      removeLocalPreview(file.name);
      setErrorMessage(null);
    } catch (error: Error | unknown) {
      setLocalPreviewStatus("error", file.name);
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("Failed to upload photo. Please try again.");
      }
    }
  };

  const handlePhotoRetry = (photoToUpload: File) => {
    if (photoToUpload) {
      handlePhotoUpload(photoToUpload);
    }
  };

  const handlePhotoProcessing = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (!files) return;

    const photoToUpload = files[0];

    // Handling server upload
    handlePhotoUpload(photoToUpload);

    // Creating local preview
    const localPreviewUrl = URL.createObjectURL(photoToUpload);
    setLocalPreviews((prev) => [
      ...prev,
      {
        id: photoToUpload.name,
        src: localPreviewUrl,
        title: photoToUpload.name,
        file: photoToUpload,
        status: "loading",
      },
    ]);

    event.target.value = "";
  };

  return (
    <>
      <Flex gap="s100" overflowX="auto">
        {uploadedPhotos.map(({ id, src, title, status }) => (
          <PhotoPreview
            key={id}
            src={src}
            title={title}
            isLoading={status === "loading"}
            hasError={status === "error"}
            action={() => handlePhotoRemove(id)}
            actionLabel={`Remove ${title}`}
            actionIcon={<TrashIcon boxSize={6} />}
          />
        ))}
        {localPreviews.map(({ id, src, title, file, status }) => (
          <PhotoPreview
            key={id}
            src={src}
            title={title}
            isLoading={status === "loading"}
            hasError={status === "error"}
            action={() => handlePhotoRetry(file)}
            actionLabel="Retry upload"
            actionIcon={<RetryIcon boxSize={6} />}
          />
        ))}
        {canAddMore && (
          <AddPhotoButton
            label={label}
            fullSize={photosDisplayedLength === 0}
            onClick={handlePhotoProcessing}
            isDisabled={isDisabled}
          />
        )}
      </Flex>
      {errorMessage && (
        <DetailL color="red.500" mt="s100" fontWeight="bold">
          {errorMessage}
        </DetailL>
      )}
    </>
  );
}
